import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SponsorBlock } from "@/components/site/SponsorBlock";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { ArticleCard } from "@/components/site/ArticleCard";
import { Markdown } from "@/components/site/Markdown";
import { ShareBar } from "@/components/site/ShareBar";
import { PreferredSourceButton } from "@/components/site/PreferredSourceButton";
import { CiteThis } from "@/components/site/CiteThis";
import { getArticleBySlug, listRelatedArticles, listCategories } from "@/lib/articles.functions";
import { listAuthorsPublic } from "@/lib/authors.functions";
import { formatDate, SITE } from "@/lib/site";
import { filterCompetitorUrls, stripCompetitorSourcesFromMarkdown } from "@/lib/competitor-domains";
import { COVER_SIZES, coverSrcSet, transformCoverUrl } from "@/lib/cover-image-url";
import { resolveAuthor, getDefaultAuthorName, type AuthorRow } from "@/lib/authors";
import { AuthorBio } from "@/components/site/AuthorBio";
import { useRecordArticleView } from "@/hooks/useRecordArticleView";

import logoAsset from "@/assets/logo-512.png.asset.json";

const PUBLISHER_LOGO_URL = `${SITE.url}${logoAsset.url}`;

export const Route = createFileRoute("/article/$slug")({
  loader: async ({ params, context }) => {
    const result = await getArticleBySlug({ data: { slug: params.slug } });
    if (!result) throw notFound();
    const [, , , authors] = await Promise.all([
      context.queryClient.prefetchQuery({
        queryKey: ["article", params.slug],
        queryFn: () => Promise.resolve(result),
      }),
      context.queryClient.prefetchQuery({
        queryKey: ["related", result.article.id],
        queryFn: () =>
          listRelatedArticles({
            data: { categoryId: result.article.category_id, excludeId: result.article.id },
          }),
      }),
      context.queryClient.prefetchQuery({
        queryKey: ["categories"],
        queryFn: () => listCategories(),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["authors", "public"],
        queryFn: () => listAuthorsPublic(),
      }),
    ]);
    return { ...result, authors: (authors ?? []) as AuthorRow[] };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { article } = loaderData;
    const title = article.seo_title || article.title;
    const desc = article.seo_description || article.excerpt || SITE.description;
    const url = `${SITE.url}/article/${params.slug}`;
    // Serve social crawlers a 1200-wide WebP variant of the cover so
    // Product Hunt / Slack / LinkedIn / X previews don't have to download
    // the full-resolution original.
    const ogImageUrl = article.cover_image_url
      ? transformCoverUrl(article.cover_image_url, { width: 1200 }) ?? article.cover_image_url
      : null;
    const og = ogImageUrl
      ? [{ property: "og:image", content: ogImageUrl }]
      : [];
    const faqItems = Array.isArray((article as any).faq)
      ? ((article as any).faq as { question: string; answer: string }[])
      : [];
    const wordCount = (article as any).word_count as number | undefined;
    const focusKeyword = (article as any).focus_keyword as string | undefined;
    const secondary = ((article as any).secondary_keywords as string[] | undefined) ?? [];
    const entities = ((article as any).entities as string[] | undefined) ?? [];
    const authors = (loaderData as any).authors as AuthorRow[] | undefined;
    const authorName = article.author_name ?? getDefaultAuthorName(authors);
    const author = resolveAuthor(authorName, authors);
    const authorJsonLd = author
      ? {
          "@type": "Person",
          name: author.name,
          url: author.url,
          sameAs: author.sameAs,
        }
      : { "@type": "Organization", name: authorName };
    const coverImages = article.cover_image_url
      ? [
          transformCoverUrl(article.cover_image_url, { width: 1600, height: 900 }),
          transformCoverUrl(article.cover_image_url, { width: 1600, height: 1200 }),
          transformCoverUrl(article.cover_image_url, { width: 1200, height: 1200 }),
        ].filter((u): u is string => Boolean(u))
      : undefined;
    return {
      meta: [
        { title: `${title} — ${SITE.name}` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(ogImageUrl
          ? [{ name: "twitter:image", content: ogImageUrl }]
          : []),
        { property: "article:published_time", content: article.published_at ?? "" },
        { property: "article:modified_time", content: article.updated_at ?? "" },
        { property: "article:author", content: author?.linkedin ?? author?.url ?? authorName },
        ...(loaderData.category
          ? [{ property: "article:section", content: loaderData.category.name }]
          : []),
        ...entities.slice(0, 8).map((tag) => ({ property: "article:tag", content: tag })),
        ...og,
        ...(ogImageUrl
          ? [
              { property: "og:image:alt", content: article.cover_image_alt ?? article.title },
            ]
          : []),
      ],

      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
        ...(article.cover_image_url
          ? [{
              rel: "preload",
              as: "image",
              href: transformCoverUrl(article.cover_image_url, { width: 1280 }) ?? article.cover_image_url,
            } as any]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.title,
            description: desc,
            inLanguage: "en",
            isAccessibleForFree: true,
            datePublished: article.published_at,
            dateModified: article.updated_at,
            author: authorJsonLd,
            publisher: {
              "@type": "Organization",
              name: SITE.name,
              url: SITE.url,
              logo: {
                "@type": "ImageObject",
                url: PUBLISHER_LOGO_URL,
                width: 512,
                height: 512,
              },
            },
            image: coverImages,
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            articleSection: loaderData.category?.name,
            wordCount,
            keywords: focusKeyword ? [focusKeyword, ...secondary].join(", ") : undefined,
            about: entities.slice(0, 10),
            audience: {
              "@type": "BusinessAudience",
              audienceType: "Local SEO agencies, GBP specialists, and local business owners",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              ...(loaderData.category
                ? [{
                    "@type": "ListItem",
                    position: 2,
                    name: loaderData.category.name,
                    item: `${SITE.url}/category/${loaderData.category.slug}`,
                  }]
                : []),
              {
                "@type": "ListItem",
                position: loaderData.category ? 3 : 2,
                name: article.title,
                item: url,
              },
            ],
          }),
        },
        ...(faqItems.length > 0
          ? [{
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqItems.map((f) => ({
                  "@type": "Question",
                  name: f.question,
                  acceptedAnswer: { "@type": "Answer", text: f.answer },
                })),
              }),
            }]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SpeakableSpecification",
            cssSelector: [".js-tldr", ".js-key-takeaways", "h1"],
          }),
        },
      ],
    };
  },

  component: ArticlePage,
});


function ArticleBody() {
  const params = Route.useParams();
  const { data } = useSuspenseQuery({
    queryKey: ["article", params.slug],
    queryFn: () => getArticleBySlug({ data: { slug: params.slug } }),
  });
  const { data: related } = useSuspenseQuery({
    queryKey: ["related", data!.article.id],
    queryFn: () =>
      listRelatedArticles({
        data: { categoryId: data!.article.category_id, excludeId: data!.article.id },
      }),
  });
  const { data: categories } = useSuspenseQuery({
    queryKey: ["categories"],
    queryFn: () => listCategories(),
  });
  const { data: authors } = useSuspenseQuery({
    queryKey: ["authors", "public"],
    queryFn: () => listAuthorsPublic(),
  });

  const { article, category } = data!;
  useRecordArticleView(article.slug);
  const authorRows = (authors ?? []) as AuthorRow[];
  const catMap = new Map(categories.map((c) => [c.id, { slug: c.slug, name: c.name }] as const));


  return (
    <article className="mx-auto max-w-3xl px-5 sm:px-8 pt-8 pb-16">
      <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-accent">Home</Link></li>
          {category && (
            <>
              <li aria-hidden>/</li>
              <li>
                <Link to="/category/$slug" params={{ slug: category.slug }} className="hover:text-accent">
                  {category.name}
                </Link>
              </li>
            </>
          )}
        </ol>
      </nav>
      <header className="text-center mt-6">
        {category && (
          <Link to="/category/$slug" params={{ slug: category.slug }} className="eyebrow hover:text-accent">
            {category.name}
          </Link>
        )}
        <h1 className="headline-display mt-4 text-4xl sm:text-6xl">{article.title}</h1>
        {article.subtitle && (
          <p className="mt-5 font-serif italic text-xl text-muted-foreground leading-snug">
            {article.subtitle}
          </p>
        )}
        <div className="mt-6 text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-center gap-3 flex-wrap">
          <span>
            By{" "}
            {(() => {
              const a = resolveAuthor(article.author_name, authorRows);
              const name = a?.name ?? article.author_name ?? getDefaultAuthorName(authorRows);
              return a ? (
                <a href={a.url} className="hover:text-accent underline-offset-2 hover:underline" rel="author">
                  {name}
                </a>
              ) : (
                <span>{name}</span>
              );
            })()}
          </span>
          <span aria-hidden>·</span>
          <span>{formatDate(article.published_at)}</span>
          {article.reading_minutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{article.reading_minutes} min read</span>
            </>
          ) : null}
        </div>
      </header>

      {article.cover_image_url && (() => {
        const sources = coverSrcSet(article.cover_image_url, {
          sizes: COVER_SIZES.hero,
          largest: 1600,
        });
        return (
          <figure className="mt-10 -mx-5 sm:mx-0">
            <img
              src={sources?.src ?? article.cover_image_url}
              srcSet={sources?.srcSet}
              sizes={sources?.sizes}
              alt={article.cover_image_alt ?? article.title}
              className="w-full aspect-[16/9] object-cover"
              width={1600}
              height={900}
              fetchPriority="high"
              loading="eager"
              decoding="async"
            />
            {article.cover_image_alt && (
              <figcaption className="mt-2 text-xs text-muted-foreground text-center px-5">
                {article.cover_image_alt}
              </figcaption>
            )}
          </figure>
        );
      })()}


      <div className="mt-8 pb-4 border-b border-border">
        <ShareBar url={`${SITE.url}/article/${params.slug}`} title={article.title} text={article.excerpt ?? undefined} />
      </div>

      {article.excerpt && (
        <aside className="js-tldr mt-8 border-l-4 border-accent bg-paper-soft p-5" aria-label="TL;DR">
          <p className="eyebrow">TL;DR</p>
          <p className="mt-2 text-base leading-relaxed">{article.excerpt}</p>
        </aside>
      )}

      <div className="mt-8">
        <Markdown content={stripCompetitorSourcesFromMarkdown(article.content_md)} />
      </div>

      {Array.isArray((article as any).key_takeaways) && (article as any).key_takeaways.length > 0 && (
        <aside className="js-key-takeaways mt-10 border-l-4 border-accent bg-paper-soft p-6">
          <p className="eyebrow">Key takeaways</p>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed">
            {((article as any).key_takeaways as string[]).map((t, i) => (
              <li key={i} className="flex gap-2">
                <span aria-hidden className="text-accent">→</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </aside>
      )}

      {Array.isArray((article as any).faq) && (article as any).faq.length > 0 && (
        <section className="mt-12">
          <div className="border-b border-foreground pb-3 mb-6">
            <h2 className="headline-display text-2xl">Frequently asked questions</h2>
          </div>
          <dl className="space-y-6">
            {((article as any).faq as { question: string; answer: string }[]).map((item, i) => (
              <div key={i}>
                <dt className="font-semibold text-base">{item.question}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {(() => {
        const visibleSources = filterCompetitorUrls(article.source_urls);
        if (visibleSources.length === 0) return null;
        const sourcePublishedAt = (article as any).source_published_at as string | null | undefined;
        const provenance = (article as any).provenance as { source_name?: string | null } | null;
        const sourceLabel = provenance?.source_name ?? null;
        return (
          <aside className="mt-12 border-t border-border pt-6">
            <p className="eyebrow">Sources</p>
            {sourcePublishedAt && (
              <p className="mt-2 text-xs text-muted-foreground">
                Based on{sourceLabel ? ` ${sourceLabel} — ` : " a source published "}
                {formatDate(sourcePublishedAt)}
              </p>
            )}
            <ul className="mt-3 min-w-0 space-y-1 text-sm">
              {visibleSources.map((u) => (
                <li key={u} className="min-w-0">
                  <a href={u} target="_blank" rel="noopener noreferrer" className="break-all text-ochre-deep hover:text-accent underline">
                    {u}
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        );
      })()}


      <div className="mt-10 pt-6 border-t border-border flex flex-wrap items-center gap-4">
        <ShareBar url={`${SITE.url}/article/${params.slug}`} title={article.title} text={article.excerpt ?? undefined} />
        <CiteThis
          title={article.title}
          url={`${SITE.url}/article/${params.slug}`}
          author={article.author_name ?? getDefaultAuthorName(authorRows)}
          publishedDate={article.published_at}
        />
      </div>

      {(() => {
        const a = resolveAuthor(article.author_name, authorRows);
        return a ? <AuthorBio author={a} /> : null;
      })()}

      <PreferredSourceButton placement="article" className="mt-10" />

      <SponsorBlock variant="article" />

      <NewsletterCTA />


      {related.length > 0 && (
        <section className="mt-16">
          <div className="border-b border-foreground pb-3 mb-8">
            <h2 className="headline-display text-2xl">Related reading</h2>
          </div>
          <div className="grid gap-8 sm:grid-cols-3">
            {related.map((a) => (
              <ArticleCard key={a.id} article={a} categories={catMap} variant="compact" />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

function ArticlePage() {
  return (
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <main id="main">
        <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading…</div>}>
          <ArticleBody />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}
